import { useEffect, useMemo, useRef, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import to from 'await-to-js'
import uniq from 'lodash/uniq'

import { useGetCurrentUserQuery, useGetPermissionResolutionLazyQuery, UserPermissionsFragmentDoc } from '@acre/graphql'
import type { PermissionScopeType } from '@acre/graphql/constants'

import { mergePermissions } from '@broker-crm-utils/permissions'

import { excludeInputsBasedOnGates, mergeObjectArrays, transformPermissionInputs } from './permission.helpers'
import type { PermissionInputWithAssignees } from './permission.types'

export interface UsePermissionResolutionProps {
  inputs: PermissionInputWithAssignees[]
  exceptions?: { gates: PermissionGateName[] }
  skip?: boolean
}

const MAX_PERMISSION_RETRIES = 3
const RETRY_BASE_DELAY_MS = 1000

type PermissionDiffableParams = { inputs: PermissionInputWithAssignees[]; exceptions?: { gates: PermissionGateName[] } }

const diffInputs = (oldInputs?: PermissionDiffableParams, newInputs?: PermissionDiffableParams) => {
  if (oldInputs === newInputs) {
    return false
  }

  if (
    oldInputs?.inputs.length !== newInputs?.inputs.length ||
    oldInputs?.exceptions?.gates?.length !== newInputs?.exceptions?.gates?.length
  ) {
    return true
  }

  return JSON.stringify(oldInputs) !== JSON.stringify(newInputs)
}

const EMPTY_ARRAY: unknown[] = []

const usePermissionResolution = ({ inputs, exceptions, skip }: UsePermissionResolutionProps) => {
  const { data: currentUser, error: userError, loading: userLoading } = useGetCurrentUserQuery()

  const client = useApolloClient()

  const currentUserId = currentUser?.currentUser?.id

  const [isInitialCallComplete, setIsInitialCallComplete] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error>()
  const [gates, setGates] = useState<{ gates: PermissionGateName[]; userIds: string[] }>()

  const hasRunRef = useRef(false)
  const runStartedRef = useRef(false)
  const shouldRerunRef = useRef(false)
  const previousInputsRef = useRef<PermissionDiffableParams>()
  const mountedRef = useRef(true)

  const userNotFound = userError || (!userLoading && !currentUser?.currentUser)

  if (!skip && userNotFound) {
    hasRunRef.current = true
  }

  const [resolvePermissions] = useGetPermissionResolutionLazyQuery()

  const readyToBuildInputs = !skip && !!currentUserId && !!inputs.length

  const inputsToTransform = useMemo(() => {
    let newInputs = [...inputs]

    if (readyToBuildInputs) {
      // if there are exceptions for gates present, we need to exclude those gates from inputs
      // and potentially inputs which contain those gates completely
      if (exceptions && exceptions.gates) {
        newInputs = excludeInputsBasedOnGates({
          inputs,
          gateExceptions: exceptions.gates,
        })
      }

      if (!newInputs.length) {
        hasRunRef.current = true
      }
    }

    if (
      previousInputsRef.current &&
      diffInputs(previousInputsRef.current, { inputs: newInputs, exceptions }) &&
      hasRunRef.current &&
      newInputs.length
    ) {
      shouldRerunRef.current = true
    }

    previousInputsRef.current = { inputs: newInputs, exceptions }

    return newInputs || []
  }, [inputs, readyToBuildInputs, exceptions])

  const [gatesByScope, setGatesByScope] = useState<{ [key in PermissionScopeType]?: Set<PermissionGateName> }>({})
  const [gatesByAssignee, setGatesByAssignee] = useState<{ [key: string]: Set<PermissionGateName> }>({})

  useEffect(() => {
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    async function fn(rawInputs: PermissionInputWithAssignees[], retryCount = 0) {
      runStartedRef.current = true

      const { permissionInputs, allGates } = transformPermissionInputs({ inputs: rawInputs })

      const [err, data] = await to(
        Promise.all(
          permissionInputs.map((input) =>
            resolvePermissions({
              variables: {
                input,
              },
            }),
          ),
        ),
      )

      if (err) {
        console.error(err)
        setError(err)
      } else {
        const hasPartialFailure = data.some((d) => !d.data)

        if (hasPartialFailure && retryCount < MAX_PERMISSION_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, RETRY_BASE_DELAY_MS * (retryCount + 1)))
          if (!mountedRef.current) return
          return fn(rawInputs, retryCount + 1)
        }

        const results = data.reduce((acc, d) => {
          if (d.data?.permissionResolution) {
            const copy = { ...d.data.permissionResolution } as ResolvedPermissionResolution

            // nullify assignee object if id is null
            if (copy.assignee?.id === null) {
              copy.assignee = null
            }

            acc.push(copy)
          }
          return acc
        }, [] as ResolvedPermissionResolution[])

        // we only receive an array of has_permissions from the api,
        // the order of these are expected to be the same as the inputs.
        // we need to merge the results from the api request and what was
        // sent so that the frontend code can have both the frontend
        // permissions gates as well as the corresponding response together
        const mergedArray = results?.length && allGates?.length ? mergeObjectArrays(results, allGates) : []

        const newCurrentUserPermissions = mergedArray.filter((m) => m.assignee?.id === currentUserId || !m.assignee)

        let updatedUserPermissions: Pick<User, 'permissions'> | null = null

        if (newCurrentUserPermissions.length) {
          updatedUserPermissions = client.cache.updateFragment<Pick<User, 'permissions'>, Pick<User, 'permissions'>>(
            {
              id: `User:${currentUserId!}`,
              fragment: UserPermissionsFragmentDoc,
              fragmentName: 'UserPermissions',
            },
            (data) => {
              const permissions = mergePermissions(
                data?.permissions ?? [],
                newCurrentUserPermissions,
              ) as ResolvedPermissionResolution[]

              return {
                ...data,
                permissions,
              }
            },
          )
        }

        if (!newCurrentUserPermissions.length || updatedUserPermissions) {
          const emptyResult = { gates: [] as PermissionGateName[], userIds: [] as string[] }

          const localGatesByScope: { [key in PermissionScopeType]?: Set<PermissionGateName> } = {}
          const localGatesByAssignee: { [key: string]: Set<PermissionGateName> } = {}

          // when using this component in the code, we will most commonly
          // need to check which permission gates have validated permissions
          // the validated permission gates are returned here
          const _gates = mergedArray?.reduce((acc, element) => {
            let result = acc

            if (element && element.gate && element.has_permission) {
              result.gates.push(element.gate)

              if (element.assignee?.id) {
                result.userIds.push(element.assignee?.id)
              }

              if (element.assignee?.id) {
                const assigneeId = element.assignee.id

                if (!localGatesByAssignee[assigneeId]) {
                  localGatesByAssignee[assigneeId] = new Set()
                }

                if (element.assignee.id) {
                  localGatesByAssignee[assigneeId]?.add(element.gate)
                }
              }

              if (element.scope?.type) {
                const scopeType = element.scope.type as PermissionScopeType

                if (!localGatesByScope[scopeType]) {
                  localGatesByScope[scopeType] = new Set()
                }

                if (element.scope.id) {
                  localGatesByScope[scopeType]?.add(element.gate)
                }
              }
            }
            return result
          }, emptyResult)

          _gates.gates = uniq(_gates.gates)

          setGatesByScope((prev) => ({ ...prev, ...localGatesByScope }))
          setGatesByAssignee((prev) => ({ ...prev, ...localGatesByAssignee }))
          setGates(_gates)
        }
      }

      shouldRerunRef.current = false
      runStartedRef.current = false
      hasRunRef.current = true

      setIsInitialCallComplete(true)
      setLoading(false)
    }

    // if there are no inputs and hasRunRef.current is true, it means we've marked this hook and complete because it doesn't need to run
    if (!skip && currentUserId && !inputsToTransform?.length && hasRunRef.current) {
      setIsInitialCallComplete(true)
    }

    if (
      !skip &&
      currentUserId &&
      inputsToTransform?.length &&
      !runStartedRef.current &&
      (!hasRunRef.current || shouldRerunRef.current)
    ) {
      setLoading(true)
      fn(inputsToTransform)
    }
  }, [exceptions, client, resolvePermissions, currentUserId, skip, inputsToTransform])

  return {
    isInitialCallComplete: userNotFound || isInitialCallComplete,
    error,
    loading,
    allGates: !gates?.gates || error ? (EMPTY_ARRAY as PermissionGateName[]) : gates?.gates,
    gates: !gates?.gates || error ? (EMPTY_ARRAY as PermissionGateName[]) : gates?.gates,
    usersThatHavePermissions: !gates?.userIds || error ? (EMPTY_ARRAY as string[]) : gates?.userIds,
    gatesByScope,
    gatesByAssignee,
  }
}

export default usePermissionResolution
